import ParticipantSourceSelector from "../../groups/ParticipantSourceSelector";
import {
  Box,
  FormControlLabel,
  Switch,
  Typography
  } from "@mui/material";
import { CategoryTypes } from "../../../interfaces/enums";
import { ParticipantsManagerProps } from "../types/PresentationFormTypes";
import { ParticipantsSelector } from "../../shared/ParticipantsSelector";
import { ParticipantWithId } from "../../../interfaces/IParticipants";
import { useGroupsContext } from "../../../contexts/GroupsContext";
/**
 * ============================================================================
 * PARTICIPANTS MANAGER - KOMPONENT ZARZĄDZANIA ZAWODNIKAMI
 * ============================================================================
 *
 * CEL KOMPONENTU:
 * - Zarządza wyborem zawodników do prezentacji
 * - Obsługuje tryb edycji (wybór) i podglądu (tylko wyświetlanie)
 * - Umożliwia przełączanie między filtrowanymi a wszystkimi zawodnikami
 * - Wyświetla informacje o zawodnikach (imię, nazwisko, data urodzenia)
 * - Integruje się z komponentem ParticipantsSelector
 *
 * ARCHITEKTURA:
 * - Container Component: Łączy logikę z komponentem ParticipantsSelector
 * - Conditional Rendering: Różne widoki dla trybu edycji i podglądu
 * - Material-UI: Używa komponentów MUI do spójnego wyglądu
 * - Switch Control: Umożliwia przełączanie trybu filtrowania
 *
 * PRZEPŁYW DANYCH:
 * 1. Otrzymuje listę dostępnych i wybranych zawodników
 * 2. Obsługuje zmiany w wyborze przez callback onSelectionChange
 * 3. Zarządza stanem przełącznika "pokaż wszystkich"
 * 4. Wyświetla odpowiedni widok w zależności od trybu (edycja/podgląd)
 *
 * TRYBY DZIAŁANIA:
 * - Edycja: Używa ParticipantsSelector do wyboru zawodników
 * - Podgląd: Wyświetla listę wybranych zawodników w formie kart
 *
 * FUNKCJONALNOŚCI:
 * - Switch "Pokaż wszystkich": Przełącza między filtrowanymi a wszystkimi zawodnikami
 * - Formatowanie etykiet: "Imię Nazwisko | Data urodzenia"
 * - Responsive design: Dostosowuje się do różnych rozmiarów ekranu
 */

// import { formatDate } from "../../../utils/dates";

export default function ParticipantsManager({
  availableParticipants,
  selectedParticipants,
  onSelectionChange,
  showAll,
  onShowAllChange,
  editableMode = true,
  category,
  participantSource = "participants",
  selectedGroupId = null,
  onParticipantSourceChange,
  onGroupChange,
}: ParticipantsManagerProps) {
  const { groups } = useGroupsContext();

  const showAllSwitchVisible = category?.type === CategoryTypes.OTHER && (category?.ageDeviationPercentage || 0) > 0;

  return (
    <Box sx={{ mb: 3 }}>
      {/* ========================================================================
          WYBÓR ŹRÓDŁA ZAWODNIKÓW (ZAWODNICY VS GRUPY)
          ======================================================================== */}

      <Box sx={{ mb: 3 }}>
        <ParticipantSourceSelector
          source={participantSource}
          onSourceChange={onParticipantSourceChange || (() => {})}
          disabled={!editableMode}
          groups={groups}
          selectedGroupId={selectedGroupId}
          onGroupChange={onGroupChange || (() => {})}
        />
      </Box>
      {/* ========================================================================
          PRZEŁĄCZNIK "POKAŻ WSZYSTKICH ZAWODNIKÓW" (ukryty dla kategorii SOLO)
          ======================================================================== */}

      {/* Ukryj przełącznik dla kategorii SOLO - zawsze pokazujemy wszystkich dostępnych zawodników */}
      {showAllSwitchVisible && (
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showAll} // Stan przełącznika (true = pokaż wszystkich)
                onChange={(e) => onShowAllChange(e.target.checked)} // Handler zmiany stanu
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#6366f1", // Niebieski kolor gdy włączony
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#6366f1", // Niebieskie tło ścieżki
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: "#6c6c6c" }}>
                Pokaż wszystkich zawodników
              </Typography>
            }
          />
        </Box>
      )}

      {/* ========================================================================
          WYBÓR ZAWODNIKÓW (TRYB EDYCJI) LUB PODGLĄD (TRYB TYLKO DO ODCZYTU)
          ======================================================================== */}

      {editableMode ? (
        /* ========================================================================
            TRYB EDYCJI - UŻYJ PARTICIPANTS SELECTOR
            ======================================================================== */

        <ParticipantsSelector<ParticipantWithId>
          allItems={availableParticipants} // Lista wszystkich dostępnych zawodników
          selectedItems={selectedParticipants} // Lista aktualnie wybranych zawodników
          setSelectedItems={onSelectionChange} // Handler zmiany wyboru
          getLabel={(participant: ParticipantWithId) =>
            // Formatowanie etykiety zawodnika: "Imię Nazwisko | Data urodzenia"
            participant.firstName +
            " " +
            participant.lastName +
            " | " +
            new Date(participant.birthDate).toLocaleDateString()
          }
        />
      ) : (
        /* ========================================================================
            TRYB PODGLĄDU - WYŚWIETL LISTĘ WYBRANYCH ZAWODNIKÓW
            ======================================================================== */

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Wybrani zawodnicy:
          </Typography>
          {selectedParticipants.map((participant) => (
            <Box
              key={participant.id}
              sx={{ mb: 1, p: 1, backgroundColor: "#f5f5f5", borderRadius: 1 }}
            >
              {/* Wyświetl informacje o zawodniku w formie karty */}
              {participant.firstName} {participant.lastName} |{" "}
              {new Date(participant.birthDate).toLocaleDateString()}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
